import React from 'react';

import Table from '../../Shared/UI/Table/components/Table';
import Thead from '../../Shared/UI/Table/components/Thead';
import Tbody from '../../Shared/UI/Table/components/Tbody';
import Tr from '../../Shared/UI/Table/components/Tr';
import Th from '../../Shared/UI/Table/components/Th';
import Td from '../../Shared/UI/Table/components/Td';
import Accordion from '../../Shared/UI/Accordion/components/Accordion';
import Select from '../../Shared/UI/Select/components/Select';
import Button from '../../Shared/UI/Button/components/Button';

import { useJobProspectsState, useJobProspectsActions } from './JobProspectsContext';
import { updateCandidateInterestLevelInJobProspect, updateJobProspectStatus } from './restApi';

const statusLabels = {
  awaiting_admin_approval: 'Awaiting Admin Approval',
  not_started: 'Not Started',
  application_in_process: 'Application In Process',
  applied_and_waiting: 'Applied And Waiting',
  interviewing: 'Interviewing',
  offer_received: 'Offer Received',
  offer_accepted: 'Offer Accepted',
  not_hiring: 'Not Hiring',
  no_offer: 'No Offer',
  decided_against: 'Decided Against',
  waiting_for_decision: 'Waiting For Decision',
};

const JobProspectsTable = () => {
  const jobProspectsState = useJobProspectsState();
  const { onResponse, onError, openConfirmClosedJobModal, openGenerateResumeModal } = useJobProspectsActions();
  const authenticityToken = document.querySelector("meta[name='csrf-token']").getAttribute("content");
  const generatingJobProspectIds = jobProspectsState?.generatingJobProspectIds || [];

  const renderResumeCell = (jobProspect) => {
    if (generatingJobProspectIds.includes(jobProspect.id)) {
      return <span style={{ color: 'var(--color-text-muted)', fontStyle: 'italic' }}>Generating…</span>;
    }

    const state = jobProspect.resumeGenerationState;

    if (state === 'generated') {
      return <a href={`/resumes/${jobProspect.resumeId}?from=/job_prospects`}>View Resume</a>;
    }

    if (state === 'generating' || state === 'pending') {
      return <span style={{ color: 'var(--color-text-muted)', fontStyle: 'italic' }}>Generating…</span>;
    }

    if (state === 'failed') {
      return <Button variant="secondary" size="sm" onClick={() => openGenerateResumeModal(jobProspect.id)}>Retry</Button>;
    }

    return <Button variant="secondary" size="sm" onClick={() => openGenerateResumeModal(jobProspect.id)}>Generate Resume</Button>;
  };

  const handleJobProspectLinkClick = (e, jobProspect) => {
    const isJobDescriptionClosed = jobProspect?.jobDescriptionClosedAt != null;

    if (isJobDescriptionClosed) {
      e.preventDefault();
      openConfirmClosedJobModal(jobProspect);
    }
    // If not closed, let the default link behavior happen
  };

  const renderStatusGroup = (jobProspectStatusGroup) => {
    const label = statusLabels[jobProspectStatusGroup?.status] || jobProspectStatusGroup?.status;
    const count = jobProspectStatusGroup.jobProspects.length;

    return (
      <Accordion
        key={jobProspectStatusGroup?.status}
        title={`${label} (${count})`}
      >
        <Table>
          <Thead>
            <Tr>
              <Th>Prospect</Th>
              <Th>Organization</Th>
              <Th>Added By</Th>
              <Th>Your Interest Level</Th>
              <Th>Status</Th>
              <Th>Resume</Th>
            </Tr>
          </Thead>
          <Tbody>
            {jobProspectStatusGroup.jobProspects.map(jobProspect => {
              const isJobDescriptionClosed = jobProspect?.jobDescriptionClosedAt != null;

              return (
                <Tr key={jobProspect.id} style={isJobDescriptionClosed ? { backgroundColor: 'var(--color-bg-subtle)' } : {}}>
                <Td>
                  <a
                    href={jobProspect.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => handleJobProspectLinkClick(e, jobProspect)}
                  >
                    {jobProspect.title}
                  </a>
                </Td>
                <Td>
                  <a href={jobProspect.biopharmaGroupLink} target="_blank" rel="noopener noreferrer">{jobProspect.biopharmaGroupName}</a>
                </Td>
                <Td>{jobProspect.addedByName}</Td>
                <Td>
                  <Select
                    onChange={(e) => {
                      updateCandidateInterestLevelInJobProspect({
                        onResponse,
                        onError,
                        body: {
                          authenticityToken,
                          selectedCandidateInterestLevel: e.target.value,
                          jobProspectId: jobProspect.id,
                        },
                      });
                    }}
                    value={jobProspect?.candidateInterestLevel}
                  >
                    {jobProspectsState?.candidateInterestLevelOptions?.map((option) => (
                      <option key={option.value} value={option.value}>{option.displayValue}</option>
                    ))}
                  </Select>
                </Td>
                <Td>
                  <Select
                    onChange={(e) => {
                      updateJobProspectStatus({
                        onResponse,
                        onError,
                        body: {
                          authenticityToken,
                          selectedJobProspectStatus: e.target.value,
                          jobProspectId: jobProspect.id,
                        },
                      });
                    }}
                    value={jobProspect?.status}
                  >
                    {jobProspectsState?.jobProspectStateOptions?.map((option) => (
                      <option key={option.value} value={option.value}>{option.displayValue}</option>
                    ))}
                  </Select>
                </Td>
                <Td>
                  {renderResumeCell(jobProspect)}
                </Td>
              </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Accordion>
    );
  };

  const jobProspectStatusGroups = jobProspectsState?.jobProspectStatusGroups || [];
  const hasActiveJobProspects = jobProspectStatusGroups.some(
    group => group?.jobProspects && group.jobProspects.length > 0 && group.isAStatusGroupForActiveJobProspects
  );

  return (
    <>
      {!hasActiveJobProspects && (
        <p style={{ textAlign: 'center', fontWeight: 'bold' }}>No active job prospects available</p>
      )}
      {jobProspectStatusGroups.map((jobProspectStatusGroup) =>
        renderStatusGroup(jobProspectStatusGroup)
      )}
    </>
  );
};

export default JobProspectsTable;
